import { useEffect, useState } from "react";
import { useRouteError } from "react-router-dom";
import {
  isChunkLoadError,
  shouldAutoReload,
  attemptChunkErrorReload,
  clearReloadAttempt,
} from "@/utils/chunkErrorHandler";

const ErrorBoundary = () => {
  const error = useRouteError();
  const [hasAttemptedReload, setHasAttemptedReload] = useState(false);

  const isChunkError = isChunkLoadError(error);

  useEffect(() => {
    if (isChunkError && shouldAutoReload()) {
      attemptChunkErrorReload();
    } else if (isChunkError) {
      setHasAttemptedReload(true);
    }
  }, [isChunkError, error]);

  const handleManualReload = () => {
    clearReloadAttempt();
    window.location.reload();
  };

  // Show chunk-specific error UI
  if (isChunkError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md px-4">
          <h1 className="text-2xl font-bold mb-4 text-foreground">
            App Updated
          </h1>
          <p className="text-muted-foreground mb-6">
            {hasAttemptedReload
              ? "We tried to reload automatically but the issue persists. Please try refreshing the page."
              : "A new version is available. Reloading..."}
          </p>
          {hasAttemptedReload && (
            <button
              onClick={handleManualReload}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Refresh Page
            </button>
          )}
        </div>
      </div>
    );
  }

  // Default error UI for other errors
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-4">Oops!</h1>
        <p className="text-gray-600">
          {error instanceof Error ? error.message : "Something went wrong"}
        </p>
      </div>
    </div>
  );
};

export default ErrorBoundary;
